"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Search } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-sm" />
        <div className="h-9 w-16 bg-muted/30 animate-pulse rounded-sm" />
      </div>;
  }
  const desiredOrder = ["Lab Values", "FAQ", "About", "Contact"];
  const sortedMenuItems = [...menuItems].sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.label);
    const indexB = desiredOrder.indexOf(b.label);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {sortedMenuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="hover:!bg-transparent focus:!bg-transparent relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-foreground transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 bg-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full group-hover:left-0 group-focus:w-full group-focus:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 bg-background border-b border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "min-h-[60px] h-[60px] md:h-[60px] shadow-medium" : "min-h-[72px] h-[72px] md:h-[80px]"}`}>
      <div className="container mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-4 sm:gap-6 md:gap-8 py-3 md:py-0">
        <Link to="/" className="hidden md:flex shrink-0 items-center">
          <img src="https://medi42.com/wp-content/uploads/2026/01/F2929525-AF87-4860-A5A8-EBD773233E93.jpeg" alt="LabExplain" className="!opacity-[1] !rounded-4xl !m-0 !h-[54px] !w-[175px]" />
        </Link>

        <MenuProvider menu_id="3" className="flex-1 flex justify-end">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="hidden md:flex items-center gap-2 sm:gap-3 md:gap-4">
          <div className={`flex items-center gap-2 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${searchOpen ? "w-[160px] sm:w-[200px]" : "w-auto"}`}>
            {searchOpen && <Input type="search" placeholder="Search..." className="h-9 text-sm" autoFocus onBlur={() => setSearchOpen(false)} />}
          </div>
        </div>
      </div>
    </header>;
}